/**
 * The transport every endpoint goes through.
 *
 * Three things happen here that would otherwise be repeated at each of the
 * seventy-odd call sites the MobX stores had:
 *
 *   1. The bearer token is attached from Redux state, not read out of storage
 *      at call time. The previous stores each cached their own copy and a
 *      `SetAllAccessTokens` helper existed to push a new one into all seven of
 *      them. Miss one and that store kept calling with the previous session's
 *      token.
 *
 *   2. The API's response envelope is unwrapped, so callers see `data`. The
 *      envelope's `message` and pagination `meta` are forwarded on the RTK
 *      Query meta object, where `transformResponse` can still reach them.
 *
 *   3. A 401 triggers exactly one refresh attempt, and concurrent 401s wait on
 *      that same attempt rather than each firing their own. Without the mutex a
 *      dashboard that fires six queries on mount sends six refreshes, five of
 *      which present an already-rotated token and are rejected, which signs the
 *      user out during a perfectly ordinary token expiry.
 */

import {
    fetchBaseQuery,
    type BaseQueryFn,
    type FetchArgs,
    type FetchBaseQueryError,
    type FetchBaseQueryMeta,
} from "@reduxjs/toolkit/query";

import { env } from "../lib/env";
import Mutex from "../lib/mutex";
import { sessionCleared, tokensRefreshed } from "../store/slices/authSlice";
import type { APIResponse, Meta, TokenPair } from "./types";

/** What the envelope carried alongside `data`, forwarded to transformResponse. */
export interface EnvelopeMeta extends Partial<FetchBaseQueryMeta> {
    envelope?: { message: string; meta?: Meta };
}

interface RootStateShape {
    auth: { accessToken: string | null; refreshToken: string | null };
}

const rawBaseQuery = fetchBaseQuery({
    baseUrl: env.apiBaseUrl,
    prepareHeaders: (headers, { getState }) => {
        const token = (getState() as RootStateShape).auth.accessToken;
        if (token) headers.set("Authorization", `Bearer ${token}`);
        return headers;
    },
});

const refreshMutex = new Mutex();

/**
 * Pulls the human-readable message out of the API envelope.
 *
 * The API always sends one and it is written for the person who will read it,
 * so surfacing `error.data.message` beats any string this client could invent.
 */
export function errorMessage(
    error: unknown,
    fallback = "Something went wrong. Please try again."
): string {
    if (!error || typeof error !== "object") return fallback;

    const candidate = error as FetchBaseQueryError;
    if ("status" in candidate) {
        if (candidate.status === "FETCH_ERROR") {
            return "Could not reach the server. Check your connection and try again.";
        }
        if (candidate.status === "TIMEOUT_ERROR") {
            return "The server took too long to respond. Please try again.";
        }
        const body = candidate.data as Partial<APIResponse<unknown>> | undefined;
        if (body && typeof body.message === "string" && body.message) {
            return body.message;
        }
    }
    const loose = error as { message?: unknown };
    if (typeof loose.message === "string" && loose.message) return loose.message;
    return fallback;
}

/**
 * The HTTP status behind a failed request, when there is one.
 *
 * A number for a real response, a string for RTK Query's own failures
 * ("FETCH_ERROR", "PARSING_ERROR", "TIMEOUT_ERROR", "CUSTOM_ERROR"), and
 * undefined for anything that is not a query error at all. Callers that care
 * about a specific code should compare against the number and let everything
 * else fall through to their general path.
 */
export function errorStatus(error: unknown): number | string | undefined {
    if (!error || typeof error !== "object") return undefined;
    const candidate = error as FetchBaseQueryError;
    return "status" in candidate ? candidate.status : undefined;
}

/** True when a value looks like the API's response envelope. */
function isEnvelope(value: unknown): value is APIResponse<unknown> {
    return (
        typeof value === "object" &&
        value !== null &&
        "status" in value &&
        "message" in value
    );
}

/**
 * Unwraps the envelope and re-authenticates once on a 401.
 *
 * A refresh failure is terminal: the refresh token is the last credential we
 * hold, so if the API rejects it there is nothing left to try and the session
 * is cleared. That sign-out belongs here rather than in a component, because a
 * component that forgets leaves the app looping on 401s.
 */
export const baseQueryWithReauth: BaseQueryFn<
    string | FetchArgs,
    unknown,
    FetchBaseQueryError,
    Record<string, unknown>,
    EnvelopeMeta
> = async (args, api, extraOptions) => {
    // Wait out an in-flight refresh before issuing the request at all, so a
    // request starting mid-rotation uses the new token rather than racing to a
    // 401 of its own.
    await refreshMutex.waitForUnlock();

    let result = await rawBaseQuery(args, api, extraOptions);

    if (result.error?.status === 401) {
        const { refreshToken } = (api.getState() as RootStateShape).auth;

        if (!refreshToken) {
            api.dispatch(sessionCleared());
            return result;
        }

        if (!refreshMutex.isLocked()) {
            const release = await refreshMutex.acquire();
            try {
                const refreshed = await rawBaseQuery(
                    { url: "auth/refresh", method: "POST", body: { refreshToken } },
                    api,
                    extraOptions
                );
                const envelope = refreshed.data as
                    | APIResponse<TokenPair>
                    | undefined;

                if (envelope?.data?.accessToken) {
                    api.dispatch(tokensRefreshed(envelope.data));
                } else {
                    api.dispatch(sessionCleared());
                    return result;
                }
            } finally {
                release();
            }
        } else {
            await refreshMutex.waitForUnlock();
        }

        result = await rawBaseQuery(args, api, extraOptions);
    }

    if (isEnvelope(result.data)) {
        const envelope = result.data;
        return {
            data: envelope.data ?? null,
            meta: {
                ...result.meta,
                envelope: { message: envelope.message, meta: envelope.meta },
            },
        };
    }

    return result;
};

export default baseQueryWithReauth;
